import type { BinaryTreeNodeData } from './BinaryTreeNode';

/**
 * Function to recursively "inject" new children into the tree.
 *
 * @param {SnippetTreeNodeData[]} nodes - The nodes to inject into.
 * @param {string} parentPath - The path to the parent node.
 * @param {SnippetTreeNodeData[]} children - The new children to inject.
 * @returns {SnippetTreeNodeData[]} - The new tree with the children injected.
 */
export function injectChildren(
  nodes: BinaryTreeNodeData[],
  parentPath: string,
  children: BinaryTreeNodeData[],
): BinaryTreeNodeData[] {
  return nodes.map((node) => {
    //  If the node's path matches the parent path, we need to inject the new children into it.
    if (node.path === parentPath) {
      return { ...node, children, hasLoaded: true };
    }
    return node.children
      ? {
          ...node,
          //  Recursively inject the new children into the node's children.
          children: injectChildren(node.children, parentPath, children),
        }
      : node;
  });
}

// Helper to process children and track if a node was removed
function processNodes(
  childNodes: BinaryTreeNodeData[],
  targetIdentifier: string,
  targetType: 'file' | 'directory',
): { newNodes: BinaryTreeNodeData[]; hasNodeRemoved: boolean } {
  let hasNodeRemoved = false;
  const newNodes = childNodes.map((node) => {
    if (node.children) {
      // Filter out nodes with matching identifier and type (if specified)
      const filteredChildren = node.children.filter((child) => {
        if (targetType && child.type !== targetType) {
          return true;
        }
        // For files, match on revisionId; for directories, match on path
        if (child.type === 'file') {
          return child.revisionId !== targetIdentifier;
        }
        return child.path !== targetIdentifier;
      });
      const hasRemovedAtThisLevel =
        filteredChildren.length !== node.children.length;
      // Recursively process children
      const {
        newNodes: processedChildren,
        hasNodeRemoved: hasRemovedInDescendants,
      } = processNodes(filteredChildren, targetIdentifier, targetType);
      const shouldDecrement =
        hasRemovedAtThisLevel || hasRemovedInDescendants;
      if (shouldDecrement) {
        hasNodeRemoved = true;
      }
      return {
        ...node,
        children: processedChildren,
        ...(node.type === 'directory' && node.count !== undefined && shouldDecrement
          ? { count: node.count - 1 }
          : {}),
      };
    }
    return node;
  });
  return { newNodes, hasNodeRemoved };
}

/**
 * Removes a node (file or directory) from the tree and decrements directory counts as needed.
 * @param nodes The tree nodes to process.
 * @param targetIdentifier The identifier of the node to remove (revisionId for files, path for directories).
 * @param targetType Optional type of node to remove ('file' or 'directory'). If not specified, removes any matching node.
 * @returns The new tree with the node removed and counts decremented.
 */
export function removeNodeFromParent(
  nodes: BinaryTreeNodeData[],
  targetIdentifier: string,
  targetType: 'file' | 'directory',
): BinaryTreeNodeData[] {
  return processNodes(nodes, targetIdentifier, targetType).newNodes;
}

/**
 * Helper function to sort nodes by type and path. Directories will come before files, and everything is sorted alphabetically.
 * @param a - The first node to compare.
 * @param b - The second node to compare.
 * @returns -1 if a should come before b, 1 if a should come after b, 0 if they are equal.
 */
export const sortNodes = (a: BinaryTreeNodeData, b: BinaryTreeNodeData) => {
  if (a.type === 'directory' && b.type !== 'directory') {
    return -1;
  }
  if (a.type !== 'directory' && b.type === 'directory') {
    return 1;
  }
  return a.path.localeCompare(b.path);
};

/**
 * Preloading function that recursively loads directories until finding multiple children
 * @param onLoadPath - The function to load the children of a node.
 * @param startPath - The path to start preloading from.
 * @param search - The search query to filter the children.
 * @param maxDepth - The maximum depth to preload.
 */
export const preloadDirectoryTree = async (
  onLoadPath: (path?: string, search?: string) => Promise<BinaryTreeNodeData[]>,
  startPath?: string,
  search?: string,
  maxDepth: number = 5,
): Promise<{ tree: BinaryTreeNodeData[]; pathsToOpen: string[] }> => {
  const pathsToOpen: string[] = [];

  const loadRecursively = async (
    currentPath?: string,
    depth: number = 0,
  ): Promise<BinaryTreeNodeData[]> => {
    if (depth >= maxDepth) {
      return [];
    }

    try {
      const children = await onLoadPath(currentPath, search);

      // Transform children to match the expected format
      const transformedChildren = children.map((child) => ({
        ...child,
        children: child.type === 'directory' ? [] : undefined,
        path: child.type === 'file' ? `${child.path}$${child.componentId}` : child.path,
        hasLoaded: false, // Don't mark as loaded initially
      }));

      // Sort children: directories first, then alphabetically
      const sortedChildren = [...transformedChildren].sort(sortNodes);

      // Find directories among children
      const directories = sortedChildren.filter(child => child.type === 'directory');

      // If there's exactly one directory and no files, continue preloading
      if (directories.length === 1 && sortedChildren.length === 1) {
        const singleDirectory = directories[0];
        if (singleDirectory) {
          pathsToOpen.push(singleDirectory.path);

          // Recursively load this directory's children
          const subChildren = await loadRecursively(singleDirectory.path, depth + 1);

          return [{
            ...singleDirectory,
            children: subChildren,
            hasLoaded: subChildren.length > 0, // Only mark as loaded if it has children
          }];
        }
      }

      // If we have multiple children or files, stop preloading here
      return sortedChildren;
    } catch {
      return [];
    }
  };

  const tree = await loadRecursively(startPath);
  return { tree, pathsToOpen };
};
